import {
  Button,
  Grid,
  TablePagination,
  TextField,
  makeStyles,
} from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { Skeleton, Stack } from "@mui/material";
import { format, startOfDay } from "date-fns";
import {
  getDateTimeFormats,
  getDateTimeLocale,
  getInputDateFormats,
} from "../../../../../../utils/get-date-time-locale";

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useHistory } from "react-router";
import { useAuth } from "../../../../../../context/auth";
import { actionShowSpinner } from "../../../../../../redux-flow/action/Wrapper/Spinner";
import {
  ILog,
  getAllActionTypeOptions,
  getAllFunctionalityOptions,
  getAllLogs,
} from "../../../../../../services/audit.service";
import { Select } from "../../../../../Common/Select";
import { Table } from "../../../../../Common/Table";
import ToastMessage from "../../../../../Common/ToastMessage";
import ModalBase from "../../../../utils/ModalBase";
import ShowAuditLogDetail from "../../../Modals/ShowAuditLogDetail";
import Header from "../../../components/Header";
import {
  Container,
  CounterLabel,
  FetchMetaDataContainer,
  FilterButtonsContainer,
  FilterInputs,
  ListContainer,
  ListCounterContainer,
  LoadingSkeletonContainer,
} from "./styles";
const useStyles = makeStyles((theme) => ({
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
  },
  fullWidthContainer: {
    maxWidth: "100%",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "16px",
  },
}));

interface IBusinessUnitProps {
  actionShowSpinner: (value: boolean) => void;
}

export interface IProfileManagementListContext {
  listOptions: {
    filterParams: {
      name?: string;
      group?: string;
      role?: string;
    };
    pagination: {
      page: number;
      size: number;
    };
  };
  updateFilterParams: (filterParams: {
    name?: string;
    group?: string;
    role?: string;
  }) => void;
  updatePagination: (filterParams: { page: number; size: number }) => void;
}

interface IPagination {
  page: number;
  pageSize: number;
}

interface IOption {
  id: string;
  name: string;
}

interface IFilters {
  actionType?: { id: string; name: string };
  functionality?: { id: string; name: string };
  responsible?: string;
  actionDescription?: string;
  initialDate?: Date;
  finalDate?: Date;
}

const AuditList: React.FC<IBusinessUnitProps> = ({ actionShowSpinner }) => {
  const effectHasAlreadyRun = React.useRef(false);

  const { userInfo, userAccess } = useAuth() as any;
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const history = useHistory();

  const [toastMessage, setToastMessage] = useState({
    open: false,
    content: "",
    type: "",
    autoHideDuration: 4000,
  });
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<ILog[]>([]);
  const [showAuditLogsInfoModalOpened, setShowAuditLogsInfoModalOpened] =
    useState(false);
  const [showAuditLogsInfo, setShowAuditLogsInfo] = useState<
    { oldVersion: string; currentVersion: string } | undefined
  >();
  const [totalItems, setTotalItems] = useState<number | undefined>();
  const [pagination, setPagination] = useState<IPagination>({
    pageSize: 10,
    page: 0,
  });
  const [functionalityOptions, setFunctionalityOptions] = useState<IOption[]>(
    []
  );
  const [actionTypeOptions, setActionTypeOptions] = useState<IOption[]>([]);
  const [filters, setFilters] = useState<IFilters>({});

  const columns = [
    {
      title: t("softwareManagement.modules.audit.functionality").toUpperCase(),
      field: "entity",
    },
    {
      title: t("softwareManagement.modules.audit.action").toUpperCase(),
      field: "actionType",
    },
    {
      title: t("softwareManagement.modules.audit.responsible").toUpperCase(),
      field: "responsibleUserName",
    },
    {
      title: t("softwareManagement.modules.audit.dateTime").toUpperCase(),
      field: "date",
    },
    {
      title: t(
        "softwareManagement.modules.audit.actionDescription"
      ).toUpperCase(),
      field: "actionDescription",
    },
  ];

  useEffect(() => {
    if (!userInfo?.master) {
      history.push("/");
    }
  }, [userInfo, userAccess]);

  const fetchLogs = useCallback(
    async (
      { page, pageSize }: { page: number; pageSize: number },
      filters?: IFilters
    ) => {
      actionShowSpinner(true);
      setLoading(true);

      try {
        const formattedFilters = {
          ...filters,
          actionType: filters?.actionType?.id,
          functionality: filters?.functionality?.id,
        };

        const auditLogs = await getAllLogs(
          {
            page: page,
            pageSize: pageSize,
          },
          formattedFilters
        );

        const formattedLogs = auditLogs.actionsHistory.map((log) => {
          return {
            ...log,
            entity: t(`softwareManagement.modules.audit.entity.${log.entity}`),
            actionType: t(
              `softwareManagement.modules.audit.actionTypeOptions.${String(
                log.actionType
              ).toLowerCase()}`
            ),
            date: format(
              new Date(log.createdDate),
              getDateTimeFormats(i18n.language),
              { locale: getDateTimeLocale(i18n.language) }
            ),
          };
        });

        setLogs(formattedLogs);
        setTotalItems(auditLogs.count);
      } catch (error: any) {
        const [errorMessage] = error?.response?.data?.errors || [
          t("softwareManagement.modules.audit.errorFetchLogs"),
        ];

        setToastMessage({
          open: true,
          content: errorMessage,
          type: "error",
          autoHideDuration: 4000,
        });
      }

      setLoading(false);
      actionShowSpinner(false);
    },
    []
  );

  const fetchActionTypesOptions = useCallback(async () => {
    try {
      const allActionTypeOptions = await getAllActionTypeOptions();
      const allActionTypeOptionsFormatted = allActionTypeOptions.map(
        (option) => ({
          id: String(option.id),
          name: t(`${option.name}`),
        })
      );

      setActionTypeOptions(allActionTypeOptionsFormatted);
    } catch (err: any) {
      const [errorMessage] = err?.response?.data?.errors || [
        t("softwareManagement.modules.audit.errorFetchActionTypesOptions"),
      ];

      setToastMessage({
        open: true,
        content: errorMessage,
        type: "error",
        autoHideDuration: 4000,
      });
    }
  }, []);

  const fetchFunctionalityOptions = useCallback(async () => {
    const allFunctionalityOptions = await getAllFunctionalityOptions();
    const allFunctionalityOptionsFormatted = allFunctionalityOptions.map(
      (option) => ({
        id: String(option.id),
        name: `${option.name}`,
      })
    );

    setFunctionalityOptions(allFunctionalityOptionsFormatted);
  }, []);

  useEffect(() => {
    console.log("I18N: ", i18n.language);

    fetchLogs(
      { page: pagination.page, pageSize: pagination.pageSize },
      filters
    );

    if (effectHasAlreadyRun.current === false) {
      fetchActionTypesOptions();
      fetchFunctionalityOptions();

      effectHasAlreadyRun.current = true;
    }
  }, [effectHasAlreadyRun, pagination, fetchLogs, i18n]);

  const handleFilter = useCallback(() => {
    fetchLogs(
      { page: pagination.page, pageSize: pagination.pageSize },
      filters
    );
  }, [pagination, filters, fetchLogs]);

  const handleCleanFilters = useCallback(() => {
    setFilters({
      responsible: "",
      actionDescription: "",
    });
    fetchLogs({ page: pagination.page, pageSize: pagination.pageSize }, {});
  }, [pagination, fetchLogs]);

  return (
    <Container>
      <Header
        title={t("softwareManagement.modules.audit.title")}
        showVectors
        showMenuToggle
        filterInputs={
          <>
            <FilterInputs container direction="column" spacing={2}>
              <Grid style={{ padding: "0 10px 0 10px" }} item lg={4} xs={4}>
                <Select
                  id="functionality"
                  autoComplete={false}
                  options={functionalityOptions}
                  setValue={(newValue: any) =>
                    setFilters((oldValue) => {
                      return {
                        ...oldValue,
                        functionality: newValue,
                      };
                    })
                  }
                  value={filters.functionality}
                  label={t("softwareManagement.modules.audit.functionality")}
                />
              </Grid>

              <Grid style={{ padding: "0 10px 0 10px" }} item lg={4} xs={4}>
                <Select
                  id="actionType"
                  autoComplete={false}
                  options={actionTypeOptions}
                  setValue={(newValue: any) =>
                    setFilters((oldValue) => {
                      return {
                        ...oldValue,
                        actionType: newValue,
                      };
                    })
                  }
                  value={filters.actionType}
                  label={t("softwareManagement.modules.audit.actionType")}
                />
              </Grid>

              <Grid style={{ padding: "0 10px 0 10px" }} item lg={4} xs={4}>
                <TextField
                  name="responsible"
                  id="responsible"
                  label={t("softwareManagement.modules.audit.responsible")}
                  fullWidth
                  InputLabelProps={{
                    shrink:
                      filters?.responsible?.length ?? 0 > 0 ? true : false,
                  }}
                  value={filters.responsible}
                  autoComplete="nope"
                  onChange={(e) =>
                    setFilters((oldValue) => ({
                      ...oldValue,
                      responsible: e.target.value,
                    }))
                  }
                />
              </Grid>

              <Grid
                style={{ padding: "0 10px 0 10px", border: "none" }}
                item
                lg={4}
                xs={4}
              >
                <KeyboardDatePicker
                  autoOk
                  disableToolbar
                  variant="inline"
                  format={getInputDateFormats(i18n.language)}
                  id="createdIn"
                  fullWidth
                  label={t("forms.startDateLabel")}
                  value={filters.initialDate || null}
                  onChange={(date) =>
                    setFilters((oldValue) => ({
                      ...oldValue,
                      initialDate: date ? startOfDay(date) : undefined,
                    }))
                  }
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                />
              </Grid>

              <Grid style={{ padding: "0 10px 0 10px" }} item lg={4} xs={4}>
                <KeyboardDatePicker
                  autoOk
                  disableToolbar
                  variant="inline"
                  format={getInputDateFormats(i18n.language)}
                  id="createdIn"
                  fullWidth
                  label={t("forms.endDateLabel")}
                  value={filters.finalDate || null}
                  onChange={(date) =>
                    setFilters((oldValue) => ({
                      ...oldValue,
                      finalDate: date ? startOfDay(date) : undefined,
                    }))
                  }
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                />
              </Grid>

              <Grid style={{ padding: "0 10px 0 10px" }} item lg={4} xs={4}>
                <TextField
                  name="actionDescription"
                  id="actionDescription"
                  label={t(
                    "softwareManagement.modules.audit.actionDescription"
                  )}
                  fullWidth
                  InputLabelProps={{
                    shrink:
                      filters?.actionDescription?.length ?? 0 > 0
                        ? true
                        : false,
                  }}
                  value={filters.actionDescription}
                  autoComplete="nope"
                  onChange={(e) =>
                    setFilters((oldValue) => ({
                      ...oldValue,
                      actionDescription: e.target.value,
                    }))
                  }
                />
              </Grid>
            </FilterInputs>

            <FilterButtonsContainer>
              <Button
                variant="outlined"
                onClick={() => {
                  handleCleanFilters();
                }}
              >
                {t("softwareManagement.clean")}
              </Button>

              <Button
                variant="contained"
                className={classes.button}
                onClick={() => handleFilter()}
              >
                {t("softwareManagement.search")}
              </Button>
            </FilterButtonsContainer>
          </>
        }
      />

      <FetchMetaDataContainer>
        {loading ? (
          <Stack style={{ width: "100%" }} spacing={1}>
            <Skeleton
              variant={"rectangular"}
              style={{ borderRadius: "8px" }}
              width="100%"
              height={60}
            />
          </Stack>
        ) : (
          <>
            <ListCounterContainer>
              <CounterLabel>
                {totalItems} {t("softwareManagement.modules.audit.logs")}
              </CounterLabel>
            </ListCounterContainer>
          </>
        )}
      </FetchMetaDataContainer>

      <ListContainer>
        {loading ? (
          <LoadingSkeletonContainer>
            <Skeleton
              variant={"rectangular"}
              style={{ borderRadius: "8px" }}
              width="100%"
              height="80px"
            />
            <Skeleton
              variant={"rectangular"}
              style={{ borderRadius: "8px", marginTop: "6px" }}
              width="100%"
              height="80px"
            />
            <Skeleton
              variant={"rectangular"}
              style={{ borderRadius: "8px", marginTop: "6px" }}
              width="100%"
              height="80px"
            />
            <Skeleton
              variant={"rectangular"}
              style={{ borderRadius: "8px", marginTop: "6px" }}
              width="100%"
              height="80px"
            />
          </LoadingSkeletonContainer>
        ) : (
          <Grid item md={12} lg={12} className={classes.fullWidthContainer}>
            <Table
              columns={columns}
              data={logs}
              onRowClick={() => console.log("teste")}
              title={t("softwareManagement.modules.audit.logs")}
              actions={[
                {
                  icon: () => <Search />,
                  tooltip: t("softwareManagement.modules.audit.seeChangedData"),
                  onClick: (_event: any, rowData: ILog) => {
                    setShowAuditLogsInfo({
                      oldVersion: rowData.oldVersion,
                      currentVersion: rowData.currentVersion,
                    });
                    setShowAuditLogsInfoModalOpened(true);
                  },
                },
              ]}
              pageSize={pagination.pageSize}
              components={{
                Pagination: (props: any) => {
                  return (
                    <TablePagination
                      component="th"
                      count={totalItems || 0}
                      style={{ float: "right" }}
                      labelRowsPerPage={t("table.labelRowsPerPage")}
                      rowsPerPageOptions={[5, 10, 20]}
                      rowsPerPage={pagination.pageSize}
                      page={pagination.page}
                      labelDisplayedRows={(_data) => {
                        let numberOfPages = Math.ceil(
                          (totalItems || 0) / pagination.pageSize
                        );

                        if (numberOfPages <= 1) {
                          numberOfPages = 1;
                        }

                        return `${pagination.page + 1} ${t(
                          "table.labelDisplayedRows"
                        )} ${numberOfPages}`;
                      }}
                      onPageChange={(_e, page) => {
                        setPagination((oldValue) => ({
                          ...oldValue,
                          page: page,
                        }));
                      }}
                      onRowsPerPageChange={(event) => {
                        const newSize = event.target.value;

                        setPagination((oldValue) => ({
                          ...oldValue,
                          pageSize: Number(newSize),
                        }));

                        props.onChangeRowsPerPage(event);
                      }}
                      nextIconButtonProps={{
                        disabled:
                          pagination.page + 1 >=
                          Math.ceil((totalItems || 0) / pagination.pageSize),
                      }}
                      backIconButtonProps={{
                        disabled: pagination.page + 1 <= 1,
                      }}
                    />
                  );
                },
              }}
            />
          </Grid>
        )}
      </ListContainer>

      <ModalBase
        open={showAuditLogsInfoModalOpened}
        setOpen={setShowAuditLogsInfoModalOpened}
        content={
          <ShowAuditLogDetail
            setModalStatus={(status: boolean) =>
              setShowAuditLogsInfoModalOpened(status)
            }
            oldInfo={showAuditLogsInfo?.oldVersion}
            currentInfo={showAuditLogsInfo?.currentVersion}
          />
        }
      />
      <ToastMessage
        message={toastMessage.content}
        type={toastMessage.type || "error"}
        open={toastMessage.open}
        close={() => setToastMessage({ ...toastMessage, open: false })}
      />
    </Container>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  actionShowSpinner: (request: any) => dispatch(actionShowSpinner(request)),
});

const AuditListConnection = connect(null, mapDispatchToProps)(AuditList);

export default AuditListConnection;
